import React, { useState, useMemo, useEffect } from 'react';
import {
  X, Plus, Users, ChevronDown, Filter, Loader2,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button }    from '../ui/button';
import { Input }     from '../ui/input';
import { Separator } from '../ui/separator';
import { ScrollArea } from '../ui/scroll-area';
import { toast }     from 'sonner';
import { useLeads }    from '../../context/LeadsContext';
import { useTasks }    from '../../context/TasksContext';
import { useSettings } from '../../context/SettingsContext';
import { useAudiences } from '../../context/AudienceContext';
import { getDisplayName }    from '../../lib/utils';
import { getStageColorClasses } from '../../lib/stageColors';
import {
  FILTER_FIELDS, FILTER_CATEGORIES, FIELD_MAP,
  OPERATOR_LABELS, JOB_STATUS_OPTIONS, QUOTE_STATUS_OPTIONS, INVOICE_STATUS_OPTIONS,
  applyAudienceFilters,
} from '../../utils/audienceEngine';

// ── Helpers ───────────────────────────────────────────────────────────────────

const newRule = () => ({
  id:       Math.random().toString(36).slice(2),
  field:    '',
  operator: '',
  value:    '',
});

function getValueOptions(fieldKey, pipelineStages, serviceOptions) {
  switch (fieldKey) {
    case 'leadStage':    return (pipelineStages || []).map((s) => ({ value: s, label: s }));
    case 'serviceType':  return (serviceOptions  || []).map((s) => ({ value: s, label: s }));
    case 'jobStatus':    return JOB_STATUS_OPTIONS;
    case 'quoteStatus':  return QUOTE_STATUS_OPTIONS;
    case 'invoiceStatus':return INVOICE_STATUS_OPTIONS;
    default:             return [];
  }
}

// ── MultiSelect (used for "any_of" operator) ──────────────────────────────────

const MultiSelect = ({ options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const sel = Array.isArray(selected) ? selected : [];

  const toggle = (val) => {
    if (sel.includes(val)) onChange(sel.filter((v) => v !== val));
    else onChange([...sel, val]);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="multi-select-trigger"
        className="flex items-center gap-1 h-9 min-w-[160px] max-w-[220px] px-3 border border-gray-200
          rounded-lg text-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-400"
      >
        <span className="flex-1 text-left truncate text-sm text-gray-700">
          {sel.length === 0 ? 'Select values…' : sel.join(', ')}
        </span>
        <ChevronDown className="w-3.5 h-3.5 shrink-0 text-gray-400" />
      </button>

      {open && (
        <>
          {/* Click-away overlay */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 top-full mt-1 left-0 bg-white border border-gray-200
            rounded-lg shadow-lg py-1 min-w-[180px] max-h-52 overflow-auto">
            {options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-400">No options available</div>
            ) : (
              options.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    checked={sel.includes(opt.value)}
                    onChange={() => toggle(opt.value)}
                    className="rounded border-gray-300 accent-orange-500"
                  />
                  {opt.label}
                </label>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ── FilterRuleRow ─────────────────────────────────────────────────────────────

const FilterRuleRow = ({ rule, onChange, onRemove, pipelineStages, serviceOptions }) => {
  const fieldDef  = FIELD_MAP[rule.field];
  const operators = fieldDef?.operators || [];

  const valueOptions = useMemo(
    () => getValueOptions(rule.field, pipelineStages, serviceOptions),
    [rule.field, pipelineStages, serviceOptions]
  );

  const handleFieldChange = (field) => {
    const def = FIELD_MAP[field];
    onChange({ ...rule, field, operator: def?.operators[0] || '', value: '' });
  };

  const handleOperatorChange = (operator) => {
    onChange({ ...rule, operator, value: operator === 'any_of' ? [] : '' });
  };

  const renderValueInput = () => {
    if (!rule.field || !rule.operator) return null;
    if (rule.operator === 'has_none' || rule.operator === 'is_true' || rule.operator === 'is_false') return (
      <span className="text-xs text-gray-400 italic px-1">(no value needed)</span>
    );

    if (rule.operator === 'greater_than' || rule.operator === 'less_than') {
      return (
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-gray-500">$</span>
          <Input
            type="number"
            value={rule.value || ''}
            onChange={(e) => onChange({ ...rule, value: e.target.value })}
            min="0"
            placeholder="0"
            data-testid="filter-amount-input"
            className="w-28 h-9 text-sm"
          />
        </div>
      );
    }

    if (rule.operator === 'older_than_days' || rule.operator === 'newer_than_days') {
      return (
        <div className="flex items-center gap-1.5">
          <Input
            type="number"
            value={rule.value || ''}
            onChange={(e) => onChange({ ...rule, value: e.target.value })}
            min="1"
            placeholder="30"
            data-testid="filter-days-input"
            className="w-20 h-9 text-sm"
          />
          <span className="text-sm text-gray-500 whitespace-nowrap">days</span>
        </div>
      );
    }

    if (rule.field === 'tags') {
      return (
        <Input
          value={rule.value || ''}
          onChange={(e) => onChange({ ...rule, value: e.target.value })}
          placeholder="tag name"
          data-testid="filter-tag-input"
          className="h-9 w-36 text-sm"
        />
      );
    }

    if (rule.operator === 'any_of') {
      return (
        <MultiSelect
          options={valueOptions}
          selected={Array.isArray(rule.value) ? rule.value : []}
          onChange={(v) => onChange({ ...rule, value: v })}
        />
      );
    }

    // Single select
    return (
      <select
        value={rule.value || ''}
        onChange={(e) => onChange({ ...rule, value: e.target.value })}
        data-testid="filter-value-select"
        className="h-9 px-2 border border-gray-200 rounded-lg text-sm bg-white
          focus:outline-none focus:ring-2 focus:ring-orange-400 min-w-[120px]"
      >
        <option value="">Select…</option>
        {valueOptions.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  };

  return (
    <div className="flex items-center gap-2 flex-wrap group" data-testid="filter-rule-row">
      {/* Field */}
      <select
        value={rule.field || ''}
        onChange={(e) => handleFieldChange(e.target.value)}
        data-testid="filter-field-select"
        className="h-9 px-2 border border-gray-200 rounded-lg text-sm bg-white
          focus:outline-none focus:ring-2 focus:ring-orange-400 min-w-[130px]"
      >
        <option value="">Select field…</option>
        {FILTER_CATEGORIES.map((cat) => (
          <optgroup key={cat} label={cat}>
            {FILTER_FIELDS.filter((f) => f.category === cat).map((f) => (
              <option key={f.key} value={f.key}>{f.label}</option>
            ))}
          </optgroup>
        ))}
      </select>

      {/* Operator */}
      {rule.field && (
        <select
          value={rule.operator || ''}
          onChange={(e) => handleOperatorChange(e.target.value)}
          data-testid="filter-operator-select"
          className="h-9 px-2 border border-gray-200 rounded-lg text-sm bg-white
            focus:outline-none focus:ring-2 focus:ring-orange-400 min-w-[140px]"
        >
          {operators.map((op) => (
            <option key={op} value={op}>{OPERATOR_LABELS[op] || op}</option>
          ))}
        </select>
      )}

      {/* Value */}
      {rule.field && renderValueInput()}

      {/* Remove rule */}
      <button
        onClick={onRemove}
        title="Remove filter"
        data-testid="remove-filter-btn"
        className="ml-auto p-1.5 rounded-md text-gray-300 hover:text-red-500
          hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// ── AudienceBuilderModal ──────────────────────────────────────────────────────

/**
 * AudienceBuilderModal
 *
 * Two-panel modal:
 *   Left  — Name, Description, Filter Logic, Filter Rules
 *   Right — Live preview: contact count + first 20 matching leads
 *
 * Props:
 *   isOpen           — boolean
 *   onClose          — fn()
 *   existingAudience — audience document to edit (null for create)
 */
const AudienceBuilderModal = ({ isOpen, onClose, existingAudience }) => {
  const { leads }          = useLeads();
  const { tasks }          = useTasks();
  const { pipelineStages } = useSettings();
  const { createAudience, updateAudience, allQuotes, allInvoices, ensureAudienceSources } = useAudiences();

  // Subscribe quote/invoice sources on demand (lazy — audience builder only)
  useEffect(() => { ensureAudienceSources(); }, [ensureAudienceSources]);

  const isEditing = !!existingAudience?.id;

  const [name,        setName]        = useState('');
  const [description, setDescription] = useState('');
  const [filterLogic, setFilterLogic] = useState('AND');
  const [filters,     setFilters]     = useState([newRule()]);
  const [saving,      setSaving]      = useState(false);

  // Sync form state when modal opens or existingAudience changes
  useEffect(() => {
    if (!isOpen) return;
    if (isEditing) {
      setName(existingAudience.name || '');
      setDescription(existingAudience.description || '');
      setFilterLogic(existingAudience.filterLogic || 'AND');
      setFilters(
        (existingAudience.filters || []).map((f) => ({
          ...f,
          id: f.id || Math.random().toString(36).slice(2),
        }))
      );
    } else {
      setName('');
      setDescription('');
      setFilterLogic('AND');
      setFilters([newRule()]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existingAudience?.id]);

  // Derive unique service types from loaded leads
  const serviceOptions = useMemo(
    () => [...new Set((leads || []).map((l) => l.service || l.serviceType).filter(Boolean))],
    [leads]
  );

  // Live audience preview — recomputes whenever filters change
  const matchingLeads = useMemo(
    () => applyAudienceFilters(leads, tasks, filters, filterLogic, { quotes: allQuotes, invoices: allInvoices }),
    [leads, tasks, filters, filterLogic, allQuotes, allInvoices]
  );
  const previewLeads = matchingLeads.slice(0, 20);
  const overflowCount = matchingLeads.length - 20;

  const updateRule = (id, updated) =>
    setFilters((prev) => prev.map((r) => (r.id === id ? updated : r)));

  const removeRule = (id) =>
    setFilters((prev) => (prev.length <= 1 ? [newRule()] : prev.filter((r) => r.id !== id)));

  const addRule = () => setFilters((prev) => [...prev, newRule()]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Audience name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name:          name.trim(),
        description:   description.trim(),
        filters,
        filterLogic,
        estimatedSize: matchingLeads.length,
      };
      if (isEditing) {
        await updateAudience(existingAudience.id, payload);
        toast.success('Audience updated');
      } else {
        await createAudience(payload);
        toast.success(`Audience "${name.trim()}" created with ${matchingLeads.length} contacts`);
      }
      onClose();
    } catch (err) {
      console.error('[AudienceBuilderModal] Save error:', err);
      toast.error('Failed to save audience');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 pt-5 pb-4 border-b shrink-0">
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <Users className="w-5 h-5 text-orange-500" />
            {isEditing ? 'Edit Audience' : 'Create Audience'}
          </DialogTitle>
          <p className="text-sm text-gray-500 mt-0.5 font-normal">
            Define filter rules to build a reusable segment of contacts
          </p>
        </DialogHeader>

        {/* Body: two-panel layout */}
        <div className="flex flex-1 min-h-0 overflow-hidden">

          {/* ── Left: Builder panel ─────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5 border-r border-gray-100">

            {/* Name */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Audience Name *
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Stale Quotes — No Response"
                data-testid="audience-name-input"
                className="h-10"
              />
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Description
              </label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional — what is this audience for?"
                data-testid="audience-description-input"
                className="h-10"
              />
            </div>

            <Separator />

            {/* Filter Rules */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                  <Filter className="w-3.5 h-3.5" />
                  Filter Rules
                </label>

                {/* AND / OR toggle */}
                <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
                  {['AND', 'OR'].map((logic) => (
                    <button
                      key={logic}
                      type="button"
                      onClick={() => setFilterLogic(logic)}
                      data-testid={`filter-logic-${logic.toLowerCase()}`}
                      className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                        filterLogic === logic
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {logic}
                    </button>
                  ))}
                </div>
              </div>

              <p className="text-xs text-gray-400 -mt-2">
                {filterLogic === 'AND'
                  ? 'Contact must match ALL of the following rules'
                  : 'Contact must match ANY of the following rules'}
              </p>

              {/* Rules list */}
              <div className="space-y-3">
                {filters.map((rule) => (
                  <FilterRuleRow
                    key={rule.id}
                    rule={rule}
                    onChange={(updated) => updateRule(rule.id, updated)}
                    onRemove={() => removeRule(rule.id)}
                    pipelineStages={pipelineStages}
                    serviceOptions={serviceOptions}
                  />
                ))}
              </div>

              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={addRule}
                data-testid="add-filter-btn"
                className="w-full border-dashed text-gray-500 hover:text-gray-700 mt-1"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Add Filter Rule
              </Button>
            </div>
          </div>

          {/* ── Right: Live preview panel ────────────────────────────── */}
          <div className="w-80 shrink-0 flex flex-col bg-gray-50/70">
            <div className="px-5 pt-5 pb-3 border-b border-gray-200">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-semibold text-gray-700">Live Preview</h3>
                <span
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full
                    text-xs font-bold bg-orange-100 text-orange-700"
                  data-testid="audience-preview-count"
                >
                  <Users className="w-3 h-3" />
                  {matchingLeads.length.toLocaleString()} contact{matchingLeads.length !== 1 ? 's' : ''}
                </span>
              </div>
              <p className="text-xs text-gray-400">
                {matchingLeads.length === 0
                  ? 'No contacts match current filters'
                  : `Showing first ${Math.min(20, matchingLeads.length)} of ${matchingLeads.length}`}
              </p>
            </div>

            <ScrollArea className="flex-1 px-4 py-3">
              {previewLeads.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <Users className="w-10 h-10 text-gray-200 mb-3" />
                  <p className="text-sm font-medium text-gray-400">No matches</p>
                  <p className="text-xs text-gray-300 mt-0.5">Try adjusting your filter rules</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {previewLeads.map((lead) => (
                    <div
                      key={lead.id}
                      className="bg-white rounded-lg px-3 py-2.5 border border-gray-100 shadow-sm"
                      data-testid="audience-preview-lead"
                    >
                      <div className="font-medium text-sm text-gray-900 truncate">
                        {getDisplayName(lead, 'Unnamed Contact')}
                      </div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {lead.stage && (
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${getStageColorClasses(lead.stage)}`}>
                            {lead.stage}
                          </span>
                        )}
                        {(lead.service || lead.serviceType) && (
                          <span className="text-xs text-gray-400 truncate">
                            {lead.service || lead.serviceType}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}

                  {overflowCount > 0 && (
                    <div className="text-center py-3">
                      <span className="text-xs text-gray-400 font-medium">
                        +{overflowCount.toLocaleString()} more contacts
                      </span>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t bg-white px-6 py-4 flex items-center justify-between gap-4">
          <p className="text-xs text-gray-400 hidden sm:block">
            Audiences are compatible with SMS Campaigns, Email Campaigns, and Playbook triggers
          </p>
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !name.trim()}
              data-testid="save-audience-btn"
              className="text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
              ) : isEditing ? (
                'Update Audience'
              ) : (
                'Save Audience'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AudienceBuilderModal;
